import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * 結帳按鈕最多被鎖定幾毫秒。贈品加入/移除購物車是非同步的（呼叫
 * /cart/add.js 或 /cart/change.js），跟頁面第一次算繪之間有時間差；
 * snippets/cart-summary.liquid 會在這段時間差裡先鎖住結帳按鈕（見
 * data-gift-campaign-pending），避免顧客在贈品還沒處理完之前就結帳。
 * 這個逾時是最後一道保險：不管什麼原因（缺貨、網路異常…）導致贈品
 * 處理失敗或卡住，最多等這麼久就一定會解鎖，不會讓顧客完全結不了帳。
 */
const CHECKOUT_LOCK_TIMEOUT_MS = 6000;

/**
 * /cart/add.js 成功之後，緊接著呼叫 /cart.js 取得完整購物車物件，偶爾會
 * 讀到「還沒反映剛剛那筆寫入」的舊資料（Shopify 購物車 API 寫入後立即
 * 讀取的一致性延遲）。用短暫重試等它跟上，而不是直接把這筆漏掉贈品的
 * 舊資料當作最新狀態發布出去。
 */
const CART_REFETCH_MAX_ATTEMPTS = 5;
const CART_REFETCH_DELAY_MS = 300;

/**
 * 滿額贈禮（Gift with Purchase）
 *
 * 依「適用系列小計」是否達到門檻，自動把贈品加入或移出購物車。設定資料
 * （啟用開關、適用系列、門檻金額、贈品商品）來自 Shop metafield，由
 * blocks/gift-with-purchase.liquid 算好後用 data 屬性帶進來，這支 JS
 * 本身不知道也不需要知道實際的系列/門檻/贈品是什麼。
 *
 * 只在購物車頁面生效（規格範圍不含購物車抽屜）。
 *
 * 規格：docs/superpowers/specs/2026-09-14--gift-with-purchase.md
 *
 * @typedef {Object} CartLine
 * @property {number} variant_id
 * @property {number} product_id
 * @property {number} line_price
 *
 * @typedef {Object} CartState
 * @property {number} subtotalCents - 適用系列商品的小計（分為單位）
 * @property {boolean} giftInCart - 贈品是否已在購物車中
 */
class CartGiftWithPurchase extends HTMLElement {
  /** 是否有加入/移除請求正在進行中，避免同時觸發多個請求 */
  #pending = false;

  /** 請求進行中收到的下一個狀態，等目前請求結束後再處理一次 */
  /** @type {CartState | null} */
  #queuedState = null;

  /** 使用者是否曾在這次頁面瀏覽期間自己把贈品從購物車刪掉。是的話，即使
   *  購物車仍然達門檻，也不會再自動把贈品加回來，尊重使用者的選擇；
   *  重新整理頁面會建立全新的元件實例，這個記憶會自然重置。 */
  #userRemovedGift = false;

  /** 上一次已知的「贈品是否在購物車」狀態，用來判斷贈品是不是被使用者
   *  自己刪掉的（見 #handleCartUpdate）。 */
  #lastGiftInCart = false;

  /** 是否還在等第一次判斷完成（進頁面後第一次 /cart.js 還沒回來之前）。
   *  這段期間如果收到購物車異動事件，只會先記住最新狀態，不會立刻動作，
   *  避免跟 #initialEvaluate 自己的第一次判斷同時搶跑。 */
  #initializing = true;

  /** 監看結帳按鈕/提示文字有沒有又被伺服端算繪重新標記為
   *  data-gift-campaign-pending。cart-summary.liquid 完全是照伺服器上的
   *  購物車資料算「有沒有達標」跟「贈品在不在購物車」，不知道使用者是
   *  自己選擇刪掉贈品——所以使用者刪掉贈品之後，只要購物車再有任何其他
   *  異動（例如調整別的商品數量）觸發區塊重新算繪，cart-summary.liquid
   *  還是會算出「不符、要鎖住結帳」，把 data-gift-campaign-pending 加回去。
   *  這裡在 #userRemovedGift 是 true 的時候，把它立刻拿掉，讓結帳不會
   *  因為使用者自己的刪除選擇而卡住，直到使用者重新整理頁面為止。 */
  #pendingAttributeObserver = new MutationObserver(() => {
    if (this.#userRemovedGift) releaseCheckoutLock();
  });

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    this.#pendingAttributeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-gift-campaign-pending'],
      subtree: true,
    });

    // 保險逾時：不管贈品加入/移除的請求最後有沒有成功，最多鎖結帳按鈕
    // 這麼久，一定會解鎖，避免顧客完全被卡住結不了帳。
    window.setTimeout(releaseCheckoutLock, CHECKOUT_LOCK_TIMEOUT_MS);

    this.#initialEvaluate();
  }

  /**
   * 進頁面當下的第一次判斷。重新抓一次最新的購物車資料來判斷，而不是
   * 直接相信 Liquid 算繪當下的 data 屬性（頁面顯示出來到這支腳本執行
   * 之間，理論上購物車不會再變，但用即時資料判斷比較保險）。
   */
  async #initialEvaluate() {
    // 等待期間（本次 fetch 還沒回來前）如果已經收到別的元件觸發的購物車
    // 異動事件，見 #handleCartUpdate 裡的排隊邏輯，代表那筆資料比這次
    // /cart.js 更新，直接用那筆就好，不用再重複抓一次。
    const cart = await fetch('/cart.js')
      .then((response) => response.json())
      .catch(() => null);

    let initialState = this.#queuedState;
    this.#queuedState = null;

    if (!initialState) {
      // 涵蓋「活動剛啟用」或「購物車本來就已經達門檻」的情況；如果連
      // /cart.js 都抓不到（罕見），退回用 Liquid 算繪當下的資料判斷
      // 一次，總比完全不判斷好。
      initialState =
        cart && Array.isArray(cart.items)
          ? this.#computeFromCart(cart)
          : {
              subtotalCents: Number(this.dataset.collectionSubtotalCents) || 0,
              giftInCart: this.dataset.giftInCart === 'true',
            };
    }

    this.#initializing = false;
    this.#lastGiftInCart = initialState.giftInCart;
    this.#evaluate(initialState);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#pendingAttributeObserver.disconnect();
  }

  get #giftVariantId() {
    return Number(this.dataset.giftVariantId);
  }

  get #thresholdCents() {
    return Number(this.dataset.thresholdCents) || 0;
  }

  /** 購物車頁面一開始就在購物車裡、且屬於「適用系列」的商品 product_id 清單。
   *  只需要涵蓋頁面載入當下的購物車項目，因為購物車頁面本身不會新增全新的
   *  商品到購物車（只會調整/移除既有項目，或由這支元件自己加入/移除贈品）。 */
  get #eligibleProductIds() {
    return new Set(
      (this.dataset.eligibleProductIds || '')
        .split(',')
        .filter(Boolean)
        .map(Number)
    );
  }

  /**
   * 監聽主題全域的購物車異動事件（cart:update），用事件帶的最新 cart JSON
   * 重新計算一次，不依賴 DOM 屬性、不用管局部重新算繪的先後順序。
   * @param {CartAddEvent | CartUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    const cart = event.detail?.resource;
    if (!cart || !Array.isArray(cart.items)) return;

    const state = this.#computeFromCart(cart);

    // 這裡收到的事件保證不是我們自己發的（上面 event.target === this 已經
    // 擋掉了），所以只要觀察到「贈品原本在購物車、這次變成不在」，就是
    // 使用者自己按了刪除按鈕，記住這次頁面瀏覽期間不要再自動加回來。
    // 贈品又重新出現在購物車時（不管什麼原因），這個記憶就沒意義了，清掉。
    if (this.#lastGiftInCart && !state.giftInCart) {
      this.#userRemovedGift = true;
    } else if (state.giftInCart) {
      this.#userRemovedGift = false;
    }

    if (this.#initializing || this.#pending) {
      this.#queuedState = state;
      return;
    }

    this.#evaluate(state);
  };

  /**
   * @param {{items: CartLine[]}} cart
   * @returns {CartState}
   */
  #computeFromCart(cart) {
    const giftVariantId = this.#giftVariantId;
    const eligibleProductIds = this.#eligibleProductIds;

    let subtotalCents = 0;
    let giftInCart = false;

    for (const item of cart.items) {
      if (item.variant_id === giftVariantId) {
        giftInCart = true;
        continue;
      }
      if (eligibleProductIds.has(item.product_id)) {
        subtotalCents += item.line_price;
      }
    }

    return { subtotalCents, giftInCart };
  }

  /** @param {CartState} state */
  #evaluate(state) {
    this.#lastGiftInCart = state.giftInCart;

    const meetsThreshold = state.subtotalCents >= this.#thresholdCents;

    if (meetsThreshold && !state.giftInCart && !this.#userRemovedGift) {
      this.#addGift();
    } else if (!meetsThreshold && state.giftInCart) {
      this.#removeGift();
    }
  }

  #addGift() {
    if (!this.#giftVariantId) return;

    this.#pending = true;
    const giftVariantId = this.#giftVariantId;

    const body = JSON.stringify({
      id: giftVariantId,
      quantity: 1,
      sections: getCartSectionIds().join(','),
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          // 加入失敗（例如贈品缺貨或已下架）。不特別處理，維持現狀，
          // 下一次購物車異動觸發重新判斷時會再試一次。
          console.error('[cart-gift-with-purchase] failed to add gift', response);
          return;
        }

        // /cart/add.js 對單一品項的回應只有該筆行項目，不是完整購物車，
        // 需要另外取一次 /cart.js 才能拿到給其他元件用的完整 cart 物件
        // （比照 product-form.js 的 #fetchAndUpdateCartQuantity 做法）。
        // 這一步偶爾會讀到「還沒反映剛剛那筆寫入」的購物車（Shopify
        // 購物車 API 寫入後立即讀取的一致性延遲），所以要重試幾次，
        // 確認真的讀到剛加進去的贈品，才把這份資料發布出去。
        const cart = await fetchCartUntil((c) => c.items.some((item) => item.variant_id === giftVariantId));

        this.dispatchEvent(
          new CartAddEvent(cart, 'cart-gift-with-purchase', {
            source: 'cart-gift-with-purchase',
            itemCount: cart.item_count,
            variantId: String(giftVariantId),
            sections: response.sections,
          })
        );
      })
      .catch((error) => console.error('[cart-gift-with-purchase]', error))
      .finally(() => this.#settle());
  }

  #removeGift() {
    if (!this.#giftVariantId) return;

    this.#pending = true;

    const body = JSON.stringify({
      id: this.#giftVariantId,
      quantity: 0,
      sections: getCartSectionIds().join(','),
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((response) => {
        if (response.errors) {
          console.error('[cart-gift-with-purchase] failed to remove gift', response);
          return;
        }

        this.dispatchEvent(
          new CartUpdateEvent(response, 'cart-gift-with-purchase', {
            source: 'cart-gift-with-purchase',
            itemCount: response.item_count,
            variantId: String(this.#giftVariantId),
            sections: response.sections,
          })
        );
      })
      .catch((error) => console.error('[cart-gift-with-purchase]', error))
      .finally(() => this.#settle());
  }

  #settle() {
    this.#pending = false;

    // 不管這次加入/移除贈品成功還是失敗，都解鎖結帳按鈕：成功的話，
    // 隨著這次請求觸發的區塊重新算繪，cart-summary.liquid 本來就會正確
    // 算出「已經不用鎖了」；失敗的話，與其讓顧客被鎖住結不了帳，不如
    // 先放行，寧可漏掉贈品也不要卡死結帳流程。
    releaseCheckoutLock();

    if (this.#queuedState) {
      const state = this.#queuedState;
      this.#queuedState = null;
      this.#evaluate(state);
    }
  }
}

/** 強制解除 snippets/cart-summary.liquid 加上的結帳鎖定狀態（見
 *  data-gift-campaign-pending）。呼叫多次也沒關係，沒有鎖定時就什麼都
 *  不會發生。結帳按鈕本身只有在「因為贈品狀態而被鎖」時才會解除
 *  disabled，如果是因為購物車本身是空的而被鎖（原本就有的行為），
 *  不會被這裡誤解鎖。 */
function releaseCheckoutLock() {
  const checkoutButton = document.getElementById('checkout');
  if (checkoutButton instanceof HTMLButtonElement && checkoutButton.hasAttribute('data-gift-campaign-pending')) {
    checkoutButton.disabled = false;
    checkoutButton.removeAttribute('data-gift-campaign-pending');
  }

  document.querySelectorAll('[data-gift-campaign-pending]').forEach((element) => {
    element.removeAttribute('data-gift-campaign-pending');
  });
}

/**
 * 呼叫 /cart.js，如果回來的購物車不符合預期（例如剛加入的贈品還沒出現），
 * 就短暫等待後重試，最多重試 CART_REFETCH_MAX_ATTEMPTS 次。最後一次不管
 * 符不符合預期都直接回傳，不會無限重試卡住。
 * @param {(cart: any) => boolean} isReady
 * @returns {Promise<any>}
 */
async function fetchCartUntil(isReady) {
  let cart = null;

  for (let attempt = 1; attempt <= CART_REFETCH_MAX_ATTEMPTS; attempt++) {
    cart = await fetch('/cart.js')
      .then((response) => response.json())
      .catch(() => null);

    if (cart && Array.isArray(cart.items) && (isReady(cart) || attempt === CART_REFETCH_MAX_ATTEMPTS)) {
      return cart;
    }

    await wait(CART_REFETCH_DELAY_MS);
  }

  return cart;
}

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** @returns {string[]} 目前頁面上所有購物車元件的 section id（購物車頁面本身，
 *  以及可能存在的購物車抽屜），加入/移除贈品時一併帶上，讓它們的畫面都同步更新 */
function getCartSectionIds() {
  /** @type {Set<string>} */
  const ids = new Set();

  document.querySelectorAll('cart-items-component').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      ids.add(element.dataset.sectionId);
    }
  });

  return Array.from(ids);
}

if (!customElements.get('cart-gift-with-purchase')) {
  customElements.define('cart-gift-with-purchase', CartGiftWithPurchase);
}
